// HybridShareSheet.jsx - Bottom sheet that lets users choose between sharing
// a link or sharing an image, with a smart default stored in localStorage.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link2, Image as ImageIcon, X, Copy, Share as ShareIcon, Check } from "lucide-react";
import { useNativeFileShare } from "../../hooks/useNativeFileShare";
import {
  getShareModePreference,
  setShareModePreference,
  SHARE_MODE_LINK,
  SHARE_MODE_IMAGE,
} from "../../utils/sharePreference";
import { analyticsService } from "../../services/analytics";
import logger from "../../utils/logger";

const OPTION_LINK = {
  key: SHARE_MODE_LINK,
  label: "Share as Link",
  description: "Best for WhatsApp, Reddit, Threads, SMS",
  icon: <Link2 size={22} />,
};

const OPTION_IMAGE = {
  key: SHARE_MODE_IMAGE,
  label: "Share as Image",
  description: "Best for Instagram, TikTok, Discord",
  icon: <ImageIcon size={22} />,
};

/**
 * HybridShareSheet
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string} props.title - e.g. "Share this session"
 * @param {Object} props.linkShare - { url, text }
 * @param {Object} props.imageShare - { file, text, caption }
 * @param {string} props.analyticsEvent - base analytics event name
 * @param {Object} props.analyticsMeta - extra analytics fields
 * @param {Function} props.onClose
 */
function HybridShareSheet({
  isOpen,
  title = "Share",
  linkShare,
  imageShare,
  analyticsEvent = "share",
  analyticsMeta = {},
  onClose,
}) {
  const { shareNative } = useNativeFileShare();
  const [mode, setMode] = useState(SHARE_MODE_LINK);
  const [remember, setRemember] = useState(false);
  const [status, setStatus] = useState("");
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const copyTimerRef = useRef(null);
  const statusTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const saved = getShareModePreference();
      setMode(imageShare?.file ? saved || SHARE_MODE_LINK : SHARE_MODE_LINK);
      setRemember(!!saved);
      setStatus("");
      setCopied(false);
    }
  }, [isOpen, imageShare?.file]);

  useEffect(() => {
    if (!imageShare?.file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(imageShare.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageShare?.file]);

  const clearTimers = useCallback(() => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const track = useCallback(
    (channel) => {
      const eventName = mode === SHARE_MODE_IMAGE
        ? `${analyticsEvent}_image`
        : `${analyticsEvent}_link`;
      analyticsService.track(eventName, {
        ...analyticsMeta,
        channel,
      });
    },
    [analyticsEvent, analyticsMeta, mode]
  );

  const applyPreference = useCallback(() => {
    if (remember) {
      setShareModePreference(mode);
    } else {
      setShareModePreference(null);
    }
  }, [mode, remember]);

  const showStatus = useCallback(
    (message, autoClose = true) => {
      setStatus(message);
      if (autoClose) {
        clearTimers();
        statusTimerRef.current = setTimeout(() => setStatus(""), 2000);
      }
    },
    [clearTimers]
  );

  const handleCopyLink = useCallback(async () => {
    if (!linkShare?.url) return;
    try {
      await navigator.clipboard.writeText(linkShare.url);
      setCopied(true);
      track("copy_link");
      showStatus("Link copied!");
      clearTimers();
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1200);
    } catch (err) {
      logger.warn("Clipboard copy failed:", err);
      showStatus("Couldn't copy — try Share via…");
    }
  }, [linkShare, track, showStatus, onClose, clearTimers]);

  const handleCopyImage = useCallback(async () => {
    const file = imageShare?.file;
    if (!file || typeof window.ClipboardItem === "undefined") {
      showStatus("Copying images isn't supported on this browser.");
      return;
    }
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ [file.type]: file })]);
      track("copy_image");
      showStatus("Image copied!");
      clearTimers();
      copyTimerRef.current = setTimeout(() => onClose(), 1200);
    } catch (err) {
      logger.warn("Clipboard image copy failed:", err);
      showStatus("Couldn't copy the image — try Share via…");
    }
  }, [imageShare, track, showStatus, onClose, clearTimers]);

  const handleShareLinkNative = useCallback(async () => {
    const result = await shareNative({
      title,
      text: linkShare?.text || "",
      url: linkShare?.url,
    });
    if (result === "shared") {
      track("native_share_link");
      applyPreference();
      onClose();
    } else if (result === "unsupported") {
      showStatus("Sharing isn't supported — use Copy link");
    }
  }, [shareNative, title, linkShare, track, applyPreference, onClose, showStatus]);

  const handleShareImageNative = useCallback(async () => {
    const result = await shareNative({
      title,
      text: imageShare?.caption || imageShare?.text || "",
      files: imageShare?.file ? [imageShare.file] : [],
    });
    if (result === "shared") {
      track("native_share_image");
      applyPreference();
      onClose();
    } else {
      // For unsupported browsers or native share errors, fall back to copying
      // the image so the user can still paste it into their chat/app.
      await handleCopyImage();
    }
  }, [shareNative, title, imageShare, track, applyPreference, onClose, handleCopyImage]);

  const handlePrimaryShare = useCallback(() => {
    applyPreference();
    if (mode === SHARE_MODE_LINK) {
      handleShareLinkNative();
    } else {
      handleShareImageNative();
    }
  }, [mode, applyPreference, handleShareLinkNative, handleShareImageNative]);

  if (!isOpen) return null;

  const selectedOption = mode === SHARE_MODE_IMAGE ? OPTION_IMAGE : OPTION_LINK;

  return (
    <div
      className="hybrid-share-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 10000,
        padding: "0 1rem calc(1rem + env(safe-area-inset-bottom, 0px))",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="hybrid-share-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--bg-primary, white)",
          color: "var(--text-primary, #333)",
          borderRadius: "20px 20px 20px 20px",
          width: "100%",
          maxWidth: "480px",
          maxHeight: "85vh",
          overflow: "auto",
          padding: "1.25rem",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 600 }}>{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--text-secondary, #666)",
              padding: "0.25rem",
              display: "flex",
            }}
          >
            <X size={22} />
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gap: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          {[OPTION_LINK, OPTION_IMAGE].map((option) => {
            const selected = mode === option.key;
            return (
              <button
                key={option.key}
                onClick={() => setMode(option.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.875rem",
                  padding: "1rem",
                  borderRadius: "12px",
                  border: `2px solid ${selected ? "var(--pista-green-primary, #2E7D32)" : "var(--border-color, #e0e0e0)"}`,
                  backgroundColor: selected ? "rgba(46, 125, 50, 0.06)" : "var(--bg-primary, white)",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "var(--text-primary, #333)",
                  transition: "all 0.15s ease",
                }}
              >
                <span
                  style={{
                    color: selected ? "var(--pista-green-primary, #2E7D32)" : "var(--text-secondary, #666)",
                    flexShrink: 0,
                  }}
                >
                  {option.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{option.label}</div>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, #666)" }}>
                    {option.description}
                  </div>
                </div>
                {selected && (
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      backgroundColor: "var(--pista-green-primary, #2E7D32)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    <Check size={14} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "1rem",
            fontSize: "0.85rem",
            color: "var(--text-secondary, #666)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            style={{ width: "18px", height: "18px", accentColor: "var(--pista-green-primary, #2E7D32)" }}
          />
          Remember my choice for next time
        </label>

        {mode === SHARE_MODE_IMAGE && previewUrl && (
          <div
            style={{
              marginBottom: "0.75rem",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "var(--bg-tertiary, #f5f5f5)",
            }}
          >
            <img
              src={previewUrl}
              alt="Share preview"
              style={{
                width: "100%",
                display: "block",
                maxHeight: "260px",
                objectFit: "contain",
              }}
            />
          </div>
        )}

        {status && (
          <div
            style={{
              marginBottom: "0.75rem",
              padding: "0.6rem 0.75rem",
              borderRadius: "8px",
              backgroundColor: "var(--bg-tertiary, #f5f5f5)",
              color: "var(--text-primary, #333)",
              fontSize: "0.85rem",
              textAlign: "center",
            }}
          >
            {status}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "0.85rem",
              borderRadius: "10px",
              border: "1px solid var(--border-color, #ddd)",
              backgroundColor: "var(--bg-secondary, #f5f5f5)",
              color: "var(--text-primary, #333)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePrimaryShare}
            style={{
              flex: 2,
              padding: "0.85rem",
              borderRadius: "10px",
              border: "none",
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "white",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <ShareIcon size={18} />
            Share {selectedOption.label.replace("Share as ", "")}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            marginTop: "0.75rem",
          }}
        >
          {mode === SHARE_MODE_LINK ? (
            <button
              onClick={handleCopyLink}
              style={{
                flex: 1,
                padding: "0.75rem",
                borderRadius: "10px",
                border: "1px solid var(--border-color, #ddd)",
                backgroundColor: "var(--bg-primary, white)",
                color: "var(--text-primary, #333)",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.4rem",
              }}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          ) : (
            <button
              onClick={handleCopyImage}
              style={{
                flex: 1,
                padding: "0.75rem",
                borderRadius: "10px",
                border: "1px solid var(--border-color, #ddd)",
                backgroundColor: "var(--bg-primary, white)",
                color: "var(--text-primary, #333)",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.4rem",
              }}
            >
              <Copy size={16} />
              Copy image
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default HybridShareSheet;
